<template>
  <app-page class="sad-stripe" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „superadminseiten bitte ebenfalls umstellen“):
         leichter Titel, links Monat und Suche; ohne Filter und Gruppierung „Aktualisieren“ rechts in der Werkzeugleiste
         (Handy: Icon-Knopf im Kopf). Je Kunde mit Shop ein beschrifteter Knopf für den Stripe-Export statt des
         DevExtreme-Icons -->
    <template v-if="handy" #actions>
      <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="Kunde oder Nummer" @input="onSearchInput" />
    </template>
    <template v-if="!handy" #toolbar-end>
      <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
    </template>
    <div class="app-grid-fill sst-grid" :class="{ 'sst-grid--auto': handy }">
      <dx-data-grid
        :ref="gridRefName"
        :height="gridHeight"
        :columns="columns"
        :data-source="dataSource"
        :show-borders="false"
        :show-column-lines="false"
        :show-row-lines="true"
        :column-auto-width="false"
        :hover-state-enabled="!gridTouch"
        :allow-column-reordering="!gridTouch"
        :allow-column-resizing="!gridTouch"
        :remote-operations="false"
        :scrolling="scrolling"
        :element-attr="gridAttr"
        @toolbar-preparing="onToolbarPreparing"
        @content-ready="onContentReady"
      >
        <dx-pager :visible="false" />
        <dx-paging :page-size="defaultPageSize" />
        <template #nameTemplate="cell">
          <span class="sst-name">{{ cell.data.value || "–" }}</span>
        </template>
        <template #downloadTemplate="cell">
          <app-button
            icon="download"
            :loading="busyId === cell.data.data.customer_id"
            :disabled="busyId !== null && busyId !== cell.data.data.customer_id"
            @click="downloadTransactions(cell.data.data)"
          >Export herunterladen</app-button>
        </template>
      </dx-data-grid>
    </div>
    <app-grid-footer ref="fuss" :page-sizes="pageSizes">
      <template #summary>
        <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
        <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }} mit Shop</span>
      </template>
    </app-grid-footer>
    <app-toast v-model="errorToast" icon="alert" kind="error" :timeout="6000" text="Der Export konnte nicht erstellt werden." closable />
  </app-page>
</template>

<script>
import { DxDataGrid, DxPager, DxPaging } from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import moment from "moment";
import { saveAs } from "file-saver";
import { formatNumber, plural } from "@/utils/format";

export default {
  components: { DxDataGrid, DxPager, DxPaging },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Kunde", "Kunden");
    },
    summaryNoun() {
      return plural(this.summary.count, "Kunde", "Kunden");
    },
  },
  data() {
    return {
      gridRefName: "stripePaymentGrid",
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      gridAttr: {},
      summary: { count: 0, loaded: 0 },
      busyId: null,
      errorToast: false,
      columns: [
        { dataField: "customer_id", caption: "Nummer", width: 116, alignment: "left", sortOrder: "asc" },
        { dataField: "name", caption: "Kunde", minWidth: 200, cellTemplate: "nameTemplate" },
        { name: "download", caption: "Stripe-Export", width: 232, cellTemplate: "downloadTemplate", allowSorting: false },
      ],
      // Kunden mit Shop aus der öffentlichen Kundenliste (wie bisher)
      dataSource: new CustomStore({
        key: "customer_id",
        load: async () => {
          const data = (await axios.get(`${this.$store.state.baseUrl}customerpublic`)).data;
          const rows = (Array.isArray(data) ? data : []).filter((e) => e.shop_enabled == 1);
          this.loadedCount = rows.length;
          return rows;
        },
      }),
    };
  },
  created() {
    this.loadedCount = 0;
  },
  methods: {
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance;
    },
    refreshDataGrid() {
      const grid = this.grid();
      if (grid) {
        grid.refresh();
      }
    },
    onSearchInput() {
      const grid = this.grid();
      if (grid) {
        grid.searchByText(this.searchText || "");
      }
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.summary = { count, loaded: Math.max(this.searchText ? this.loadedCount : count, count) };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    // Zeitraum und Schnittstelle unverändert (stripeexport mit start/end als Unix-Zeit, signierte URL)
    async downloadTransactions(customer) {
      if (this.busyId !== null) {
        return;
      }
      this.busyId = customer.customer_id;
      try {
        const response = (
          await axios.get(
            `${this.$store.state.baseUrl}stripeexport?start=${moment(this.month + "-01").add(1, "month").utc().startOf("month").unix()}&end=${moment(this.month + "-01").add(1, "month").utc().endOf("month").unix()}&customer_id=${customer.customer_id}`
          )
        ).data;
        saveAs(response.signedUrl);
      } catch (e) {
        this.errorToast = true;
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>

<style scoped>
/* Handy: Grid in natürlicher Höhe */
.sst-grid.sst-grid--auto {
  flex: none;
}
.sst-grid ::v-deep .sst-name {
  font-weight: 500;
  color: var(--ink);
}
</style>
